import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import { useTablePreferences } from "./utils/useTablePreferences";
import DropDownComponent from "./formComponents/DropDownComponent";
import MultiSelectDropdown from "./formComponents/MultiSelectDropdown";
import QuestionPreviewModal from "./questions/QuestionPreviewModal";
import { typeLabel, readable, sectionTypeLabel } from "./questions/questionUtils";
import "../styles/ListPage.css";
import "../styles/QuestionList.css";

// Matches Excel-style scientific notation (e.g. 3.5701E+12). JS uses lowercase e
// and preserves all digits for numbers within MAX_SAFE_INTEGER, so this pattern
// only matches values that Excel has already corrupted.
const SCIENTIFIC_NOTATION_RE = /^-?\d+\.?\d*[Ee][+\-]?\d+$/;

// A stored question's HTML often contains escaped entities (&amp;, &quot;,
// &lt;, code-snippet content in particular) — stripping tags alone leaves
// those literal entity strings visible, which reads as "raw HTML" in the
// table. Decode via a detached <textarea>, the standard safe way to let the
// browser's own parser resolve entities without executing any markup.
const stripHtml = (html) => {
  if (!html) return "—";
  const noTags = html.replace(/<[^>]*>/g, " ");
  const el = document.createElement("textarea");
  el.innerHTML = noTags;
  return el.value.replace(/\s+/g, " ").trim() || "—";
};

const parseCSVFile = (file) =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,  // keep ALL values as strings — never auto-convert to numbers
      complete: resolve,
      error: reject,
    });
  });

// NOTE: intentionally does NOT include "label" — this header check is
// positional (see isHeaderValid below) and only validates the first N
// columns, so an optional trailing "label" column in newer CSVs is ignored
// by this check either way, while CSVs from before the labels feature
// (no label column at all) still validate correctly.
const EXPECTED_HEADERS = [
  "subjectId", "moduleId", "topicId", "section",
  "difficultyLevel", "questionType", "question", "questionAttachment",
  "answer", "answerAttachment", "answerExplanation", "notes",
  "questionLink", "markPositive", "markNegative",
  "option1", "option2", "option3", "option4", "option5",
];

// Matches the CSV export's column order (questionExport.js) — this is the
// file downloaded from "Update Questions via CSV" and expected back on
// re-upload. questionId leads since it's how each row is matched to an
// existing question; trailing subject/module/topic/timestamp columns in the
// export are read-only and not part of this check.
const EXPECTED_UPDATE_HEADERS = [
  "questionId",
  "subjectId", "moduleId", "topicId", "section",
  "difficultyLevel", "questionType", "question", "questionAttachment",
  "answer", "answerAttachment", "answerExplanation", "alternateAnswers",
  "notes", "questionLink", "markPositive", "markNegative",
  "option1", "option2", "option3", "option4", "option5",
];

// Shared by the Import and Update CSV dialogs — lets an admin bulk-upload
// images ahead of writing the CSV, then reference each by its own filename
// (the real base URL is fetched from the backend, never guessed/hardcoded
// here, so this hint can't drift out of sync with the actual S3 config).
const UploadImagesSection = ({ results, loading, baseUrl, onFilesSelected, inputRef, hint }) => (
  <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "16px", paddingTop: "16px" }}>
    <p style={{ fontSize: "13px", fontWeight: 600, margin: "0 0 4px" }}>Upload Images</p>
    <p style={{ fontSize: "12px", color: "#6b7280", margin: "0 0 10px" }}>
      {hint || (
        baseUrl
          ? <>Upload any images your CSV references, then use each one's filename in the sheet, e.g. <code>{baseUrl}/abc.png</code>.</>
          : <>Upload any images your CSV references, then use each one's filename in the sheet.</>
      )}
    </p>
    <button
      className="lp-btn-secondary"
      disabled={loading}
      onClick={() => inputRef.current?.click()}
    >
      {loading ? "Uploading…" : "Choose Images"}
    </button>
    <input
      type="file"
      accept="image/*"
      multiple
      ref={inputRef}
      style={{ display: "none" }}
      onChange={(e) => {
        onFilesSelected(e.target.files);
        e.target.value = "";
      }}
    />
    {results && results.length > 0 && (
      <ul style={{ listStyle: "none", margin: "10px 0 0", padding: 0, fontSize: "12px", maxHeight: "140px", overflowY: "auto" }}>
        {results.map((r, i) => (
          <li key={`${r.filename}-${i}`} style={{ padding: "2px 0", color: r.status === "uploaded" ? "#15803d" : "#b91c1c" }}>
            {r.status === "uploaded" ? "✓" : "✗"} {r.filename}
            {r.status === "rejected" && r.message ? ` — ${r.message}` : ""}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const QuestionList = (props) => {
  const { showCheckbox, selectedChange, onSelectedChange, previouslySelected, removeChanged, onRemoveChanged,
          showAskedFilters = false, askedBatchId } = props;
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const { rowsPerPage, setRowsPerPage, sortConfig, setSortConfig } = useTablePreferences("table_prefs_questions");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filterOptions, setFilterOptions] = useState({});
  const [importError, setImportError] = useState("");
  const [exportError, setExportError] = useState("");
  const [filters, setFilters] = useState({ questionType: "", difficultyLevel: "", topic: "", section: "", module: "", subject: "", labels: [], connected: "" });
  // "" | "notAsked" | "alreadyAsked". The two boxes are mutually exclusive —
  // a question cannot be both — so they share one value rather than two flags
  // that could contradict each other and return nothing.
  const [askedStatus, setAskedStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [importFailModal, setImportFailModal] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [validateLoading, setValidateLoading] = useState(false);
  const [validatedFile, setValidatedFile] = useState(null);
  const [showReadyDialog, setShowReadyDialog] = useState(false);
  const [validationCount, setValidationCount] = useState(0);
  const [importJobId, setImportJobId] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [validateJobId, setValidateJobId] = useState(null);
  const [validateProgress, setValidateProgress] = useState(null);
  const [pendingFile, setPendingFile] = useState(null); // file held between validate→import
  // ── Update via CSV (separate from the insert-CSV flow above) ─────────────
  const [showUpdateCsvModal, setShowUpdateCsvModal] = useState(false);
  const [updateValidateJobId, setUpdateValidateJobId] = useState(null);
  const [updateValidateProgress, setUpdateValidateProgress] = useState(null);
  const [updateApplyJobId, setUpdateApplyJobId] = useState(null);
  const [updateApplyProgress, setUpdateApplyProgress] = useState(null);
  const [updateValidatedFile, setUpdateValidatedFile] = useState(null);
  const [updatePendingFile, setUpdatePendingFile] = useState(null);
  const [showUpdateReadyDialog, setShowUpdateReadyDialog] = useState(false);
  const [updateValidationCount, setUpdateValidationCount] = useState(0);
  // ── Bulk image upload (shared by both the Import and Update CSV dialogs) ──
  // Images referenced in a CSV cell by their hosted URL — e.g. abc.png must be
  // uploaded here first, then referenced as `${imageUploadBaseUrl}/abc.png`.
  const [imageUploadResults, setImageUploadResults] = useState(null);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  const [imageUploadBaseUrl, setImageUploadBaseUrl] = useState("");
  const API_URL = process.env.REACT_APP_API_URL;
  const filterChangedRef = useRef(false);
  const fileInputRef = useRef(null);
  const updateFileInputRef = useRef(null);
  const imageFilesInputRef = useRef(null);
  const pollRef = useRef(null);
  const validatePollRef = useRef(null);
  const updateValidatePollRef = useRef(null);
  const updateApplyPollRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch the real image base URL once so the "Upload Images" hint is never
  // a guessed/hardcoded domain that could drift from the actual S3 config.
  useEffect(() => {
    fetch(`${API_URL}/api/questions-import-images/base-url`)
      .then((res) => res.json())
      .then((data) => { if (data.success) setImageUploadBaseUrl(data.baseUrl); })
      .catch(() => {});
  }, [API_URL]);

  const difficultyClass = (level) => {
    const map = { Easy: "ql-easy", Medium: "ql-medium", Hard: "ql-hard", "Very Hard": "ql-very-hard", Professional: "ql-professional" };
    return `ql-badge${level ? ` ${map[level] || ""}` : ""}`;
  };

  // The options depend on what is currently selected, so this refetches
  // whenever a filter changes. Subject narrows Module, Module narrows Topic,
  // and Labels/Difficulty/Type only ever offer values a reachable question
  // actually has. The dependency is the serialized selection rather than the
  // filters object itself, so an unrelated re-render does not refetch.
  const selectedForOptions = JSON.stringify({
    subject: filters.subject || "",
    module: filters.module || "",
    topic: filters.topic || "",
    section: filters.section || "",
    difficultyLevel: filters.difficultyLevel || "",
    questionType: filters.questionType || "",
    labels: (filters.labels || []).map((l) => l.value).join(","),
  });

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await fetch(`${API_URL}/api/filter`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ filterscreen: "questions", selected: JSON.parse(selectedForOptions) }),
        });
        const data = await res.json();
        const formatted = {};
        for (let key in data) {
          formatted[key] = data[key]
            .filter((item) => item !== null && item !== undefined)
            .map((item) => {
              // subject/module/topic come as {label, value} objects from the backend
              if (item && typeof item === "object" && item.label && item.value) {
                return item;
              }
              const str = String(item).trim();
              return str ? { label: str, value: str } : null;
            })
            .filter(Boolean);
        }
        setFilterOptions(formatted);
      } catch (err) {
        console.error("Error fetching filter options:", err);
      }
    };
    fetchFilterOptions();
  }, [selectedForOptions, API_URL]);

  // Poll the backend for import job progress while a job is active
  useEffect(() => {
    if (!importJobId) return;

    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/import-status/${importJobId}`);
        if (!res.ok) return; // transient network error — keep polling
        const data = await res.json();
        if (!data.success) return;
        setImportProgress(data.job);
        if (data.job.status === "done") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setImportJobId(null);
          fetchQuestions();
          // Auto-dismiss the success card after 6 seconds
          setTimeout(() => setImportProgress(null), 6000);
        } else if (data.job.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setImportJobId(null);
          // Card stays visible until the admin dismisses it
        }
      } catch {
        // Transient error — keep polling
      }
    };

    poll(); // immediate first check
    pollRef.current = setInterval(poll, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [importJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for validation job progress
  useEffect(() => {
    if (!validateJobId) return;

    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/import-status/${validateJobId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.success) return;
        setValidateProgress(data.job);

        if (data.job.status === "done") {
          clearInterval(validatePollRef.current);
          validatePollRef.current = null;
          setValidateJobId(null);
          setValidateProgress(null);
          // Validation passed — show "Ready to Import" dialog
          setValidatedFile(pendingFile);
          setValidationCount(data.job.count);
          setShowReadyDialog(true);
          setPendingFile(null);
        } else if (data.job.status === "failed") {
          clearInterval(validatePollRef.current);
          validatePollRef.current = null;
          setValidateJobId(null);
          setValidateProgress(null);
          setImportError(data.job.error || "Validation failed.");
          setImportFailModal(true);
          setPendingFile(null);
        }
      } catch {
        // Transient error — keep polling
      }
    };

    poll();
    validatePollRef.current = setInterval(poll, 2000);
    return () => { if (validatePollRef.current) clearInterval(validatePollRef.current); };
  }, [validateJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for update-validate job progress
  useEffect(() => {
    if (!updateValidateJobId) return;

    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/questions-update-status/${updateValidateJobId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.success) return;
        setUpdateValidateProgress(data.job);

        if (data.job.status === "done") {
          clearInterval(updateValidatePollRef.current);
          updateValidatePollRef.current = null;
          setUpdateValidateJobId(null);
          setUpdateValidateProgress(null);
          setUpdateValidatedFile(updatePendingFile);
          setUpdateValidationCount(data.job.count);
          setShowUpdateReadyDialog(true);
          setUpdatePendingFile(null);
        } else if (data.job.status === "failed") {
          clearInterval(updateValidatePollRef.current);
          updateValidatePollRef.current = null;
          setUpdateValidateJobId(null);
          setUpdateValidateProgress(null);
          setImportError(data.job.error || "Validation failed.");
          setImportFailModal(true);
          setUpdatePendingFile(null);
        }
      } catch {
        // Transient error — keep polling
      }
    };

    poll();
    updateValidatePollRef.current = setInterval(poll, 2000);
    return () => { if (updateValidatePollRef.current) clearInterval(updateValidatePollRef.current); };
  }, [updateValidateJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for update-apply job progress
  useEffect(() => {
    if (!updateApplyJobId) return;

    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/questions-update-status/${updateApplyJobId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.success) return;
        setUpdateApplyProgress(data.job);
        if (data.job.status === "done") {
          clearInterval(updateApplyPollRef.current);
          updateApplyPollRef.current = null;
          setUpdateApplyJobId(null);
          fetchQuestions();
        } else if (data.job.status === "failed") {
          clearInterval(updateApplyPollRef.current);
          updateApplyPollRef.current = null;
          setUpdateApplyJobId(null);
        }
      } catch {
        // Transient error — keep polling
      }
    };

    poll();
    updateApplyPollRef.current = setInterval(poll, 2000);
    return () => { if (updateApplyPollRef.current) clearInterval(updateApplyPollRef.current); };
  }, [updateApplyJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shared by the live list fetch and CSV export so a filter (including
  // labels, an array field) is always serialized to query params the same
  // way in both places.
  const buildQuestionQueryParams = useCallback((filtersObj, search) => {
    const queryParams = new URLSearchParams();
    if (search?.trim()) queryParams.append("search", search.trim());

    const filterKeyMap = {
      markPositive: "markPositive", markNegative: "markNegative",
      questionType: "questionType", difficultyLevel: "difficultyLevel",
      topic: "topic", section: "section", module: "module", subject: "subject",
      labels: "labels",
      connected: "connected",   // "1" = only questions used in a notes section
    };
    Object.entries(filtersObj).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Labels: array of {label,value} objects — send as comma-joined ids;
        // the backend matches with $all (must contain every selected label).
        const ids = value.map((v) => v.value).filter(Boolean);
        if (ids.length) queryParams.append(filterKeyMap[key], ids.join(","));
        return;
      }
      const v = typeof value === "string" ? value.trim() : String(value).trim();
      if (v && v !== "undefined" && v !== "null") queryParams.append(filterKeyMap[key], v);
    });
    return queryParams;
  }, []);

  // The parent builds this array inline (test.questions.map(...)), so it is a
  // new identity on every parent render. Depending on its contents instead of
  // its identity stops the list refetching when nothing has actually changed.
  const previouslySelectedKey = (previouslySelected || []).join(",");

  // One page of the list, exactly as the table asks for it — same filters,
  // search and sort. The preview modal uses it too, to step past the edge of
  // the current page into the next or previous one.
  const requestPage = useCallback(async (pageIndex) => {
    const queryParams = buildQuestionQueryParams(filters, debouncedSearchTerm);
    if (showAskedFilters && askedStatus) {
      queryParams.append("askedStatus", askedStatus);
      // Scopes the "previously asked in a test" half to this test's own batch.
      if (askedBatchId) queryParams.append("askedBatchId", askedBatchId);
    }
    queryParams.append("page", pageIndex + 1);
    queryParams.append("pageSize", rowsPerPage);
    if (sortConfig.column && sortConfig.direction) {
      queryParams.append("sortBy", sortConfig.column);
      queryParams.append("order", sortConfig.direction);
    }
    const res = await fetch(`${API_URL}/api/questions?${queryParams.toString()}`);
    const response = await res.json();
    return { rows: response.data || [], total: response.pagination?.totalItems || 0 };
  }, [rowsPerPage, debouncedSearchTerm, filters, sortConfig, askedStatus, askedBatchId, showAskedFilters, buildQuestionQueryParams]);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const { rows, total } = await requestPage(page);
      let updatedQuestions = rows;
      if (showCheckbox && previouslySelected?.length > 0) {
        const selectedQs = updatedQuestions.filter((q) => previouslySelected.includes(q._id));
        const nonSelectedQs = updatedQuestions.filter((q) => !previouslySelected.includes(q._id));
        updatedQuestions = [
          ...selectedQs.map((q) => ({ ...q, isSelected: true })),
          ...nonSelectedQs.map((q) => ({ ...q, isSelected: false })),
        ];
      }
      updatedQuestions = updatedQuestions.map((q, idx) => ({ ...q, isSelected: q.isSelected ?? false, originalIndex: idx }));
      setQuestions(updatedQuestions);
      setTotalCount(total);
    } catch (err) {
      console.error("Fetch Error:", err);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, requestPage, previouslySelectedKey, showCheckbox]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  useEffect(() => {
    if (filterChangedRef.current) { setPage(0); filterChangedRef.current = false; }
  }, [filters, debouncedSearchTerm, askedStatus]);

  // Subject -> Module -> Topic -> Section. Changing one clears everything
  // below it, because the old child value belongs to the previous parent and
  // would otherwise silently filter the list down to nothing.
  const DEPENDENT_FILTERS = {
    subject: ["module", "topic", "section"],
    module: ["topic", "section"],
    topic: ["section"],
  };

  const handleFilterChange = (key, value) => {
    if (filters[key] === value) return;
    filterChangedRef.current = true;
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      (DEPENDENT_FILTERS[key] || []).forEach((child) => { next[child] = ""; });
      return next;
    });
  };

  // Checking one unchecks the other; clicking the checked one clears it.
  const toggleAskedStatus = (value) => {
    filterChangedRef.current = true;
    setAskedStatus((prev) => (prev === value ? "" : value));
  };

  const clearAllFilters = () => {
    filterChangedRef.current = true;
    setFilters({ questionType: "", difficultyLevel: "", topic: "", section: "", module: "", subject: "", labels: [], connected: "" });
    setAskedStatus("");
  };

  const activeFilterCount =
    ["questionType", "difficultyLevel", "topic", "section", "module", "subject", "connected"]
      .filter((k) => filters[k]).length + (filters.labels?.length ? 1 : 0) + (askedStatus ? 1 : 0);

  const onSort = (column) => {
    setPage(0);
    setSortConfig((prev) => {
      if (prev.column === column) {
        if (prev.direction === "asc") return { column, direction: "desc" };
        if (prev.direction === "desc") return { column: null, direction: null };
      }
      return { column, direction: "asc" };
    });
  };

  const thClass = (col) =>
    sortConfig.column === col
      ? sortConfig.direction === "asc" ? "lp-sort-asc" : "lp-sort-desc"
      : "";

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  // The clicked row opens in the preview modal (index within this page).
  const [previewIndex, setPreviewIndex] = useState(null);
  const handleRowClick = (idx) => setPreviewIndex(idx);
  const handleAddQuestion = () => navigate("/questions/add");

  const handleExportCSV = async () => {
    const queryParams = buildQuestionQueryParams(filters, debouncedSearchTerm);
    setExportError("");
    try {
      const response = await fetch(`${API_URL}/api/questions-export?${queryParams.toString()}`, { headers: { Accept: "text/csv" } });
      if (!response.ok) {
        // The export route explains itself on 404 (nothing matched) and 413 (too
        // large to send in one file) — show that rather than a generic failure.
        let message = "Could not export the questions. Please try again.";
        try {
          const body = await response.json();
          if (body?.message) message = body.message;
        } catch { /* non-JSON body — keep the generic message */ }
        throw new Error(message);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      link.href = url;
      link.download = `questions-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("CSV download error:", error);
      setExportError(error.message);
    }
  };

  // ── Shared client-side CSV checks (headers + scientific notation) ────────
  const runClientValidation = async (file, onError) => {
    let parsed;
    try {
      parsed = await parseCSVFile(file);
    } catch (err) {
      onError("Could not read the CSV file: " + err.message);
      return false;
    }
    if (parsed.errors.length > 0) {
      const e0 = parsed.errors[0];
      onError(`CSV structural error on row ${(e0.row ?? 0) + 1}: ${e0.message}`);
      return false;
    }
    const rawHeaders = Object.keys(parsed.data[0] || {});
    const isHeaderValid = EXPECTED_HEADERS.every(
      (expected, idx) => rawHeaders[idx]?.toLowerCase() === expected.toLowerCase()
    );
    if (!isHeaderValid) {
      onError("CSV headers do not match the expected format. Please download the sample CSV.");
      return false;
    }
    // Scan every cell for Excel-corrupted scientific notation (e.g. 3.5701E+12).
    for (let rowIdx = 0; rowIdx < parsed.data.length; rowIdx++) {
      const row = parsed.data[rowIdx];
      for (const [field, value] of Object.entries(row)) {
        if (SCIENTIFIC_NOTATION_RE.test(String(value).trim())) {
          onError(
            `Row ${rowIdx + 1}, column "${field}" contains "${value}" in scientific notation. ` +
            `Excel auto-converts large numbers to this format when saving a CSV. ` +
            `To fix: in Excel select that column → Format Cells → Text, re-enter the value, then save and re-import.`
          );
          return false;
        }
      }
    }
    return true;
  };

  // ── Phase 1: start background validation job ─────────────────────────────
  const handleValidate = async (file) => {
    if (!file) return;
    setShowImportModal(false);

    const showErr = (msg) => { setImportError(msg); setImportFailModal(true); };
    const ok = await runClientValidation(file, showErr);
    if (!ok) return;

    const fd = new FormData();
    fd.append("file", file);
    setValidateLoading(true); // brief — only until jobId arrives
    try {
      const res = await fetch(`${API_URL}/api/questions-validate`, { method: "POST", body: fd });
      let response;
      try { response = await res.json(); } catch {
        showErr(`Server returned an invalid response (status ${res.status}). Please check the backend logs.`);
        return;
      }
      if (response.success && response.jobId) {
        setPendingFile(file); // hold onto the file for Phase 2
        setValidateProgress({
          status: "running", phase: "validating",
          done: 0, total: 0, currentMsg: "Reading CSV…",
        });
        setValidateJobId(response.jobId);
      } else {
        showErr(response.message || "Validation failed.");
      }
    } catch (err) {
      showErr(err.message || "Server not responding.");
    } finally {
      setValidateLoading(false);
    }
  };

  // ── Phase 2: kick off background import, get jobId immediately ───────────
  const handleConfirmImport = async () => {
    if (!validatedFile) return;
    setShowReadyDialog(false);
    const fd = new FormData();
    fd.append("file", validatedFile);
    setImportLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/questions-import`, { method: "POST", body: fd });
      let response;
      try { response = await res.json(); } catch {
        setImportError(`Server returned an invalid response (status ${res.status}). Please check the backend logs.`);
        setImportFailModal(true);
        return;
      }
      if (response.success && response.jobId) {
        // Seed progress card with what we already know from validation
        setImportProgress({
          status: "running", phase: "processing",
          done: 0, total: validationCount,
          currentMsg: "Starting import…",
        });
        setImportJobId(response.jobId);
      } else {
        setImportError(response.message || "Failed to start import.");
        setImportFailModal(true);
      }
    } catch (err) {
      setImportError(err.message || "Server not responding.");
      setImportFailModal(true);
    } finally {
      setImportLoading(false);
      setValidatedFile(null);
    }
  };

  // ── Update via CSV: phase 1 — validate ────────────────────────────────────
  const handleUpdateValidate = async (file) => {
    if (!file) return;

    const showErr = (msg) => { setImportError(msg); setImportFailModal(true); };

    let parsed;
    try {
      parsed = await parseCSVFile(file);
    } catch (err) {
      showErr("Could not read the CSV file: " + err.message);
      return;
    }
    if (parsed.errors.length > 0) {
      const e0 = parsed.errors[0];
      showErr(`CSV structural error on row ${(e0.row ?? 0) + 1}: ${e0.message}`);
      return;
    }
    const rawHeaders = Object.keys(parsed.data[0] || {});
    const isHeaderValid = EXPECTED_UPDATE_HEADERS.every(
      (expected, idx) => rawHeaders[idx]?.toLowerCase() === expected.toLowerCase()
    );
    if (!isHeaderValid) {
      showErr(
        "CSV headers do not match the expected format for updating — make sure you're re-uploading a file " +
        "downloaded from this same dialog (questionId must be the first column)."
      );
      return;
    }
    for (let rowIdx = 0; rowIdx < parsed.data.length; rowIdx++) {
      const row = parsed.data[rowIdx];
      for (const [field, value] of Object.entries(row)) {
        if (SCIENTIFIC_NOTATION_RE.test(String(value).trim())) {
          showErr(
            `Row ${rowIdx + 1}, column "${field}" contains "${value}" in scientific notation. ` +
            `Excel auto-converts large numbers to this format when saving a CSV. ` +
            `To fix: in Excel select that column → Format Cells → Text, re-enter the value, then save and re-upload.`
          );
          return;
        }
      }
    }

    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API_URL}/api/questions-update-validate`, { method: "POST", body: fd });
      let response;
      try { response = await res.json(); } catch {
        showErr(`Server returned an invalid response (status ${res.status}). Please check the backend logs.`);
        return;
      }
      if (response.success && response.jobId) {
        setUpdatePendingFile(file);
        setUpdateValidateProgress({
          status: "running", phase: "validating",
          done: 0, total: 0, currentMsg: "Reading CSV…",
        });
        setUpdateValidateJobId(response.jobId);
      } else {
        showErr(response.message || "Validation failed.");
      }
    } catch (err) {
      showErr(err.message || "Server not responding.");
    }
  };

  // ── Update via CSV: phase 2 — apply ───────────────────────────────────────
  const handleConfirmUpdateApply = async () => {
    if (!updateValidatedFile) return;
    setShowUpdateReadyDialog(false);
    const fd = new FormData();
    fd.append("file", updateValidatedFile);
    try {
      const res = await fetch(`${API_URL}/api/questions-update-apply`, { method: "POST", body: fd });
      let response;
      try { response = await res.json(); } catch {
        setImportError(`Server returned an invalid response (status ${res.status}). Please check the backend logs.`);
        setImportFailModal(true);
        return;
      }
      if (response.success && response.jobId) {
        setUpdateApplyProgress({
          status: "running", phase: "updating",
          done: 0, total: updateValidationCount,
          currentMsg: "Starting update…",
        });
        setUpdateApplyJobId(response.jobId);
      } else {
        setImportError(response.message || "Failed to start update.");
        setImportFailModal(true);
      }
    } catch (err) {
      setImportError(err.message || "Server not responding.");
      setImportFailModal(true);
    } finally {
      setUpdateValidatedFile(null);
    }
  };

  // ── Bulk image upload — shared by the Import and Update CSV dialogs ──────
  const handleUploadImages = async (files) => {
    if (!files || files.length === 0) return;
    setImageUploadLoading(true);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    try {
      const res = await fetch(`${API_URL}/api/questions-import-images`, { method: "POST", body: fd });
      const response = await res.json();
      if (response.success) {
        setImageUploadBaseUrl(response.baseUrl || "");
        setImageUploadResults((prev) => [...(prev || []), ...response.results]);
      } else {
        setImageUploadResults((prev) => [
          ...(prev || []),
          ...Array.from(files).map((f) => ({ filename: f.name, status: "rejected", message: response.message || "Upload failed." })),
        ]);
      }
    } catch (err) {
      setImageUploadResults((prev) => [
        ...(prev || []),
        ...Array.from(files).map((f) => ({ filename: f.name, status: "rejected", message: err.message || "Server not responding." })),
      ]);
    } finally {
      setImageUploadLoading(false);
    }
  };

  const handleCheckboxClick = (id) => {
    if (!showCheckbox) return;
    const updatedQuestions = [...questions];
    const index = updatedQuestions.findIndex((q) => q._id === id);
    const question = updatedQuestions[index];
    question.isSelected = !question.isSelected;
    updatedQuestions.splice(index, 1);
    if (question.isSelected) {
      updatedQuestions.unshift(question);
      onRemoveChanged((removeChanged || []).filter((rid) => rid !== id));
    } else {
      updatedQuestions.push(question);
      updatedQuestions.sort((a, b) => a.originalIndex - b.originalIndex);
      if (previouslySelected.includes(id)) {
        const newRemoveChanged = [...(removeChanged || [])];
        if (!newRemoveChanged.includes(id)) newRemoveChanged.push(id);
        onRemoveChanged(newRemoveChanged);
      }
    }
    const updatedIds = selectedChange.includes(id)
      ? selectedChange.filter((qid) => qid !== id)
      : [...selectedChange, id];
    const selectedObjs = updatedQuestions
      .filter((q) => q.isSelected)
      .map(({ _id, question, questionType, difficultyLevel, subject, topic, module: mod, section, markPositive, markNegative }) =>
        ({ _id, question, questionType, difficultyLevel, subject, topic, module: mod, section, markPositive, markNegative }));
    setQuestions(updatedQuestions);
    onSelectedChange(updatedIds, selectedObjs);
  };

  return (
    <div className="lp-page ql-page">
      {importSuccess && <div className="lp-toast">Question import successful</div>}
      {exportError && !showUpdateCsvModal && (
        <div className="lp-toast lp-toast-error" onClick={() => setExportError("")}>{exportError}</div>
      )}

      {importFailModal && (() => {
        const isConnectionErr =
          !importError ||
          importError === "Load failed" ||
          importError.toLowerCase().includes("failed to fetch") ||
          importError.toLowerCase().includes("not responding") ||
          importError.toLowerCase().includes("network");
        return (
          <div className="lp-overlay">
            <div className="lp-modal lp-modal-error">
              <h3>{isConnectionErr ? "Connection Error" : "Validation Failed"}</h3>
              {isConnectionErr ? (
                <p className="lp-error-hint">
                  Could not reach the backend server. Make sure it is running and try again.
                  {importError ? ` (${importError})` : ""}
                </p>
              ) : (
                <div className="lp-error-msg">{importError}</div>
              )}
              <div className="lp-modal-btn-row">
                <button className="lp-modal-btn" onClick={() => { setImportFailModal(false); setImportError(""); }}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showImportModal && (
        <div className="lp-overlay" onClick={() => setShowImportModal(false)}>
          <div className="lp-drop-modal" onClick={(e) => e.stopPropagation()}>
            <div
              className={`lp-drop-zone${dragOver ? " lp-drag-active" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file) handleValidate(file);
              }}
            >
              <h3>Upload CSV File</h3>
              <p>Drag a file here, or click Browse to select one.</p>
              <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
                Subject, Module, and Topic columns must contain their system IDs.
              </p>
              <div className="lp-drop-actions">
                <button className="lp-btn-primary" onClick={() => fileInputRef.current?.click()}>Browse</button>
                <button className="lp-btn-secondary" onClick={() => {
                  const link = document.createElement("a");
                  link.href = "/sample-question.csv";
                  link.download = "sample-question.csv";
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                }}>Download Sample</button>
              </div>
              <input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={(e) => handleValidate(e.target.files?.[0])}
              />
            </div>
            <UploadImagesSection
              results={imageUploadResults}
              loading={imageUploadLoading}
              baseUrl={imageUploadBaseUrl}
              onFilesSelected={handleUploadImages}
              inputRef={imageFilesInputRef}
            />
          </div>
        </div>
      )}

      {/* Validation progress card — fixed bottom-right, non-blocking */}
      {validateProgress && (
        <div className={`lp-job-card${validateProgress.status === "done" ? " lp-job-done" : validateProgress.status === "failed" ? " lp-job-failed" : ""}`}>
          <div className="lp-job-card-header">
            <span className="lp-job-card-title">
              {validateProgress.status === "done"   ? "✓ Validation Complete"
               : validateProgress.status === "failed" ? "✗ Validation Failed"
               : "Validating CSV…"}
            </span>
            {validateProgress.status === "failed" && (
              <button className="lp-job-card-dismiss" onClick={() => setValidateProgress(null)}>×</button>
            )}
          </div>

          {validateProgress.total > 0 && (
            <>
              <div className="lp-progress-track">
                <div
                  className="lp-progress-fill"
                  style={{
                    width: validateProgress.status === "done" ? "100%"
                      : `${Math.round((validateProgress.done / validateProgress.total) * 100)}%`,
                  }}
                />
              </div>
              <p className="lp-job-card-count">
                {validateProgress.done} / {validateProgress.total} questions checked
              </p>
            </>
          )}

          <p className="lp-job-card-msg">{validateProgress.currentMsg}</p>

          {validateProgress.status === "running" && (
            <p className="lp-job-card-note">Validation in progress — you can keep working.</p>
          )}
        </div>
      )}

      {/* Job status card — fixed bottom-right, non-blocking */}
      {importProgress && (
        <div className={`lp-job-card${importProgress.status === "done" ? " lp-job-done" : importProgress.status === "failed" ? " lp-job-failed" : ""}`}>
          <div className="lp-job-card-header">
            <span className="lp-job-card-title">
              {importProgress.status === "done"   ? "✓ Import Complete"
               : importProgress.status === "failed" ? "✗ Import Failed"
               : "Importing Questions…"}
            </span>
            {(importProgress.status === "done" || importProgress.status === "failed") && (
              <button className="lp-job-card-dismiss" onClick={() => setImportProgress(null)}>×</button>
            )}
          </div>

          {importProgress.status !== "failed" && (
            <>
              <div className="lp-progress-track">
                <div
                  className="lp-progress-fill"
                  style={{
                    width: importProgress.status === "done" ? "100%"
                      : importProgress.total > 0
                        ? `${Math.round((importProgress.done / importProgress.total) * 100)}%`
                        : "0%",
                  }}
                />
              </div>
              <p className="lp-job-card-count">
                {importProgress.done} / {importProgress.total} questions
              </p>
            </>
          )}

          <p className="lp-job-card-msg">{importProgress.currentMsg}</p>

          {importProgress.status === "running" && (
            <p className="lp-job-card-note">You can keep working — we'll update you here.</p>
          )}
        </div>
      )}

      {showReadyDialog && (
        <div className="lp-overlay">
          <div className="lp-modal">
            <h3>Ready to Import</h3>
            <p>
              No errors found! <strong>{validationCount}</strong> question
              {validationCount === 1 ? "" : "s"} ready to import.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "16px" }}>
              <button className="lp-btn-primary" onClick={handleConfirmImport}>Import Now</button>
              <button className="lp-modal-btn" onClick={() => { setShowReadyDialog(false); setValidatedFile(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showUpdateCsvModal && (
        <div className="lp-overlay" onClick={() => setShowUpdateCsvModal(false)}>
          <div className="lp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Update questions via csv</h3>
            <p style={{ fontSize: "13px", color: "#6b7280", margin: "8px 0 16px" }}>
              Download the currently filtered questions, edit the file, then
              upload it back to update only what changed. Filter by subject or
              module first — the whole question bank is too large for one file.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button className="lp-btn-primary" onClick={handleExportCSV}>Download CSV</button>
              <button className="lp-btn-secondary" onClick={() => updateFileInputRef.current?.click()}>Upload CSV</button>
            </div>
            {exportError && (
              <p style={{ fontSize: "13px", color: "#991b1b", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "10px 12px", margin: "14px 0 0", lineHeight: 1.5 }}>
                {exportError}
              </p>
            )}
            <input
              type="file"
              accept=".csv"
              ref={updateFileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                setShowUpdateCsvModal(false);
                if (file) handleUpdateValidate(file);
                e.target.value = "";
              }}
            />
            <UploadImagesSection
              results={imageUploadResults}
              loading={imageUploadLoading}
              baseUrl={imageUploadBaseUrl}
              onFilesSelected={handleUploadImages}
              inputRef={imageFilesInputRef}
              hint={
                imageUploadBaseUrl
                  ? <>Upload any images you want to reference, then paste a full image tag in the cell, e.g. <code>{`<img src="${imageUploadBaseUrl}/abc.png">`}</code> — update-mode stores cells as-is.</>
                  : <>Upload any images you want to reference, then paste a full image tag in the cell referencing it by filename — update-mode stores cells as-is.</>
              }
            />
          </div>
        </div>
      )}

      {/* Update-validate progress card */}
      {updateValidateProgress && (
        <div className={`lp-job-card${updateValidateProgress.status === "done" ? " lp-job-done" : updateValidateProgress.status === "failed" ? " lp-job-failed" : ""}`}>
          <div className="lp-job-card-header">
            <span className="lp-job-card-title">
              {updateValidateProgress.status === "done"   ? "✓ Validation Complete"
               : updateValidateProgress.status === "failed" ? "✗ Validation Failed"
               : "Validating CSV for update…"}
            </span>
            {updateValidateProgress.status === "failed" && (
              <button className="lp-job-card-dismiss" onClick={() => setUpdateValidateProgress(null)}>×</button>
            )}
          </div>

          {updateValidateProgress.total > 0 && (
            <>
              <div className="lp-progress-track">
                <div
                  className="lp-progress-fill"
                  style={{
                    width: updateValidateProgress.status === "done" ? "100%"
                      : `${Math.round((updateValidateProgress.done / updateValidateProgress.total) * 100)}%`,
                  }}
                />
              </div>
              <p className="lp-job-card-count">
                {updateValidateProgress.done} / {updateValidateProgress.total} questions checked
              </p>
            </>
          )}

          <p className="lp-job-card-msg">{updateValidateProgress.currentMsg}</p>

          {updateValidateProgress.status === "running" && (
            <p className="lp-job-card-note">Validation in progress — you can keep working.</p>
          )}
        </div>
      )}

      {/* Update-apply progress card */}
      {updateApplyProgress && (
        <div className={`lp-job-card${updateApplyProgress.status === "done" ? " lp-job-done" : updateApplyProgress.status === "failed" ? " lp-job-failed" : ""}`}>
          <div className="lp-job-card-header">
            <span className="lp-job-card-title">
              {updateApplyProgress.status === "done"   ? "✓ Update Complete"
               : updateApplyProgress.status === "failed" ? "✗ Update Failed"
               : "Updating Questions…"}
            </span>
            {(updateApplyProgress.status === "done" || updateApplyProgress.status === "failed") && (
              <button className="lp-job-card-dismiss" onClick={() => setUpdateApplyProgress(null)}>×</button>
            )}
          </div>

          {updateApplyProgress.status !== "failed" && (
            <>
              <div className="lp-progress-track">
                <div
                  className="lp-progress-fill"
                  style={{
                    width: updateApplyProgress.status === "done" ? "100%"
                      : updateApplyProgress.total > 0
                        ? `${Math.round((updateApplyProgress.done / updateApplyProgress.total) * 100)}%`
                        : "0%",
                  }}
                />
              </div>
              <p className="lp-job-card-count">
                {updateApplyProgress.done} / {updateApplyProgress.total} questions
              </p>
            </>
          )}

          <p className="lp-job-card-msg">{updateApplyProgress.currentMsg}</p>

          {updateApplyProgress.status === "running" && (
            <p className="lp-job-card-note">You can keep working — we'll update you here.</p>
          )}

          {updateApplyProgress.status === "done" && Array.isArray(updateApplyProgress.results) &&
            updateApplyProgress.results.some((r) => r.status === "error") && (
              <div className="lp-job-card-errors">
                <p className="lp-job-card-note" style={{ fontWeight: 600 }}>Rows that failed:</p>
                <ul style={{ margin: "4px 0 0", paddingLeft: "18px", fontSize: "12px", color: "#b91c1c" }}>
                  {updateApplyProgress.results.filter((r) => r.status === "error").map((r) => (
                    <li key={r.rowNum}>Row {r.rowNum} ({r.questionId}): {r.message}</li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      )}

      {showUpdateReadyDialog && (
        <div className="lp-overlay">
          <div className="lp-modal">
            <h3>Ready to Update</h3>
            <p>
              No errors found! <strong>{updateValidationCount}</strong> question
              {updateValidationCount === 1 ? "" : "s"} ready to check for updates.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "16px" }}>
              <button className="lp-btn-primary" onClick={handleConfirmUpdateApply}>Update Now</button>
              <button className="lp-modal-btn" onClick={() => { setShowUpdateReadyDialog(false); setUpdateValidatedFile(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="lp-header">
        <div className="lp-header-left">
          <h2 className="lp-title">All Questions</h2>
          <span className="lp-count">{totalCount} records</span>
        </div>
        {!showCheckbox && (
          <div className="lp-header-actions">
            <button className="lp-btn-primary" onClick={handleAddQuestion}>+ Add Question</button>
            <button className="lp-btn-secondary" onClick={handleExportCSV}>Export CSV</button>
            <button className="lp-btn-secondary" onClick={() => { setImageUploadResults(null); setShowImportModal(true); }}>Import CSV</button>
            <button className="lp-btn-secondary" onClick={() => { setImageUploadResults(null); setShowUpdateCsvModal(true); }}>Update questions via csv</button>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="lp-toolbar">
        <div className="lp-search-wrap">
          <span className="lp-search-icon">&#128269;</span>
          <input
            type="text"
            placeholder="Search questions…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="lp-search"
          />
        </div>
        <div className="lp-filters">
          <DropDownComponent options={filterOptions.questionType || []} value={filters.questionType} onChange={(val) => handleFilterChange("questionType", val)} placeholder="Type" />
          <DropDownComponent options={filterOptions.difficultyLevel || []} value={filters.difficultyLevel} onChange={(val) => handleFilterChange("difficultyLevel", val)} placeholder="Difficulty" />
          <DropDownComponent options={filterOptions.subject || []} value={filters.subject} onChange={(val) => handleFilterChange("subject", val)} placeholder="Subject" />
          {/* Each of these is narrowed by the one before it. When the parent
              leaves a child with nothing to offer, the child is disabled and
              says why, instead of looking like an empty broken dropdown. */}
          <DropDownComponent
            options={filterOptions.module || []}
            value={filters.module}
            onChange={(val) => handleFilterChange("module", val)}
            placeholder="Module"
            isDisabled={!(filterOptions.module || []).length}
            noOptionsMessage={filters.subject ? "No modules with questions in this subject" : "No modules"}
          />
          <DropDownComponent
            options={filterOptions.topic || []}
            value={filters.topic}
            onChange={(val) => handleFilterChange("topic", val)}
            placeholder="Topic"
            isDisabled={!(filterOptions.topic || []).length}
            noOptionsMessage={filters.module ? "No topics with questions in this module" : "No topics"}
          />
          <DropDownComponent
            options={filterOptions.section || []}
            value={filters.section}
            onChange={(val) => handleFilterChange("section", val)}
            placeholder="Section"
            isDisabled={!(filterOptions.section || []).length}
            noOptionsMessage="No sections"
          />
          <MultiSelectDropdown
            options={filterOptions.labels || []}
            value={filters.labels}
            onChange={(val) => handleFilterChange("labels", val)}
            placeholder="Labels"
            showCheckboxes
            isDisabled={!(filterOptions.labels || []).length}
          />
          <label className="ql-connected-filter" title="Only questions used in at least one notes section (shown under Connected with)">
            <input
              type="checkbox"
              checked={filters.connected === "1"}
              onChange={(e) => handleFilterChange("connected", e.target.checked ? "1" : "")}
            />
            <span>Connected to notes</span>
          </label>
          {/* Only offered where "asked" has meaning — the Add-questions picker on
              a test, which knows the batch. Neither box checked = no constraint. */}
          {showAskedFilters && (
            <div className="ql-asked-filters">
              <label className="ql-asked-option" title="Questions never used in an Example, Quiz, Practice or Assignment section, and never asked in a previous test for this batch">
                <input
                  type="checkbox"
                  checked={askedStatus === "notAsked"}
                  onChange={() => toggleAskedStatus("notAsked")}
                />
                <span>Not asked yet</span>
              </label>
              <label className="ql-asked-option" title="Questions already used in an Example, Quiz, Practice or Assignment section, or already asked in a previous test for this batch">
                <input
                  type="checkbox"
                  checked={askedStatus === "alreadyAsked"}
                  onChange={() => toggleAskedStatus("alreadyAsked")}
                />
                <span>Already asked</span>
              </label>
            </div>
          )}
          {activeFilterCount > 0 && (
            <button type="button" className="ql-clear-filters" onClick={clearAllFilters}>
              Clear filters ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="lp-table-card">
        {loading ? (
          <div className="lp-state"><div className="lp-spinner" /><p>Loading questions…</p></div>
        ) : questions.length === 0 ? (
          <div className="lp-state"><p>No questions found.</p></div>
        ) : (
          <table className="lp-table">
            <thead>
              <tr>
                {showCheckbox && <th style={{ width: 40 }}></th>}
                <th className={thClass("question")} onClick={() => onSort("question")}>Question</th>
                <th className={thClass("questionType")} onClick={() => onSort("questionType")}>Type</th>
                <th className={thClass("difficultyLevel")} onClick={() => onSort("difficultyLevel")}>Difficulty</th>
                <th className={thClass("subject")} onClick={() => onSort("subject")}>Subject › Module</th>
                <th>Connected with</th>
                <th>Labels</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((q, idx) => (
                <tr
                  key={q._id}
                  className={showCheckbox ? "" : "ql-row"}
                  onClick={showCheckbox ? () => handleCheckboxClick(q._id) : () => handleRowClick(idx)}
                >
                  {showCheckbox && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={q.isSelected} onChange={() => handleCheckboxClick(q._id)} />
                    </td>
                  )}
                  <td className="ql-question-cell"><div className="ql-question-text">{stripHtml(q.question)}</div></td>
                  <td><span className="ql-type">{typeLabel(q.questionType)}</span></td>
                  <td>{q.difficultyLevel ? <span className={difficultyClass(q.difficultyLevel)}>{q.difficultyLevel}</span> : "—"}</td>
                  <td className="ql-hier-cell">
                    {readable(q.subjectTitle || q.subject)
                      ? <div className="ql-subject">{readable(q.subjectTitle || q.subject)}</div>
                      : <span className="ql-muted">—</span>}
                    {readable(q.moduleTitle || q.module) && <div className="ql-module">{readable(q.moduleTitle || q.module)}</div>}
                  </td>
                  <td className="ql-conn-cell">
                    {/* Where the question is used: Module › Topic, then the section. Blank when unused. */}
                    {(q.connectedWith || []).length > 0 && (() => {
                      const [first, ...rest] = q.connectedWith;
                      const all = q.connectedWith.map((c) => `${c.moduleTitle ? `${c.moduleTitle} › ` : ""}${c.topicTitle} › ${c.sectionTitle}`).join("\n");
                      return (
                        <div className="ql-conn" title={all}>
                          <div className="ql-conn-path">{first.moduleTitle ? `${first.moduleTitle} › ` : ""}{first.topicTitle}</div>
                          <div className="ql-conn-section">
                            {first.sectionTitle}
                            {first.sectionType && <span className="ql-conn-type">{sectionTypeLabel(first.sectionType)}</span>}
                            {rest.length > 0 && <span className="ql-conn-more">+{rest.length} more</span>}
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td>
                    {(q.labels || []).length > 0
                      ? (
                        <div className="ql-label-chips">
                          {q.labels.map((l) => (
                            <span key={l.value} className="ql-label-chip">{l.label}</span>
                          ))}
                        </div>
                      )
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {previewIndex !== null && questions[previewIndex] && (
        <QuestionPreviewModal
          rows={questions}
          page={page}
          pageSize={rowsPerPage}
          total={totalCount}
          startIndex={previewIndex}
          fetchPage={async (p) => (await requestPage(p)).rows}
          onClose={(lastPage) => {
            setPreviewIndex(null);
            if (lastPage !== page) setPage(lastPage);
          }}
          onChanged={fetchQuestions}
        />
      )}

      {/* Pagination */}
      {!loading && questions.length > 0 && (
        <div className="lp-pagination">
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={page === 0}>← Previous</button>
          <span className="lp-page-info">Page {page + 1} of {totalPages}</span>
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))} disabled={page >= totalPages - 1}>Next →</button>
          <div className="lp-page-size">
            <label htmlFor="ql-pageSize">Rows:</label>
            <select id="ql-pageSize" value={rowsPerPage} onChange={(e) => { setPage(0); setRowsPerPage(Number(e.target.value)); }}>
              {[10, 20, 30, 50, 75, 100].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionList;

